import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import type { Product } from '../../types/product';
type ChapterRailProps = {
  product: Product;
};

/**
 * The persistent accent rail down the left edge of a product page, so you always
 * know which product you are inside.
 *
 * The travelling mark is scroll-linked rather than time-based: it reads as
 * continuous depth instead of an animation you have to wait out.
 */
export function ChapterRail({
  product
}: ChapterRailProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll();
  const smoothed = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.4
  });
  const markTop = useTransform(smoothed, [0, 1], ['4%', '86%']);
  return <div ref={ref} className={`fixed bottom-0 left-0 top-[4.25rem] z-30 hidden w-10 flex-col items-center justify-between py-6 ${product.theme.rail} lg:flex`} aria-hidden="true" data-id="element-687" data-mp="3z9l8rk0f1yl">
      <span className={`font-display text-xs font-bold ${product.theme.onSurface}`} data-id="element-688" data-mp="1u79bn9l3796f">
        {product.number}
      </span>

      <span className={`text-[10px] font-extrabold tracking-[0.2em] [writing-mode:vertical-rl] ${product.theme.onSurfaceMuted}`} data-id="element-689" data-mp="2315jdjdi8j2b">
        {product.name.toUpperCase()}
      </span>

      <span className={`relative h-1/3 w-px ${product.theme.railMark} opacity-30`} data-id="element-690" data-mp="1bare2bjk4f29">
        <motion.span style={reduceMotion ? undefined : {
        top: markTop
      }} className={`absolute -left-[1.5px] h-10 w-1 ${product.theme.railMark} opacity-100`} data-id="element-691" data-mp="1fbtg104uz57x" />
      </span>
    </div>;
}